import Box from 'UIKit/Box/Box'
import IconButton from 'UIKit/Button/IconButton'
import Page from 'UIKit/Page'
import useKeydownEventListener from 'hooks/useKeydownEventListener'
import useMatchBreakpoints from 'hooks/useMatchBreakpoints'
import { useRouter } from 'hooks/useRouter'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { Icons } from 'svgs'
import theme from 'theme'
import hunnyKeyframes from 'theme/keyframes'
import { toggleHtmlScrollable } from 'utils/html'
import SearchLayout from './SearchLayout'

const GameSearch: React.FC<{ onClose?: () => void }> = ({ onClose }) => {
  const route = useRouter()
  const [close, setClose] = useState(false)
  const isSubmitCloseRef = useRef(false)
  const { isMobile } = useMatchBreakpoints()

  useKeydownEventListener((event) => {
    if (event && event.key === 'Escape') {
      handleClose()
    }
  })

  useEffect(() => {
    return () => {
      handleClose()
    }
  }, [route])

  useEffect(() => {
    if (!isMobile) return
    toggleHtmlScrollable(false)
    return () => {
      toggleHtmlScrollable(true)
    }
  }, [isMobile])

  const handleBackdropClick = () => {
    handleClose()
  }

  const handleClose = useCallback(() => {
    if (isSubmitCloseRef.current) return
    setClose(true)
    setTimeout(() => {
      onClose()
    }, 400)

    isSubmitCloseRef.current = true
  }, [onClose, close])

  return (
    <StyledWrapper id="searchview" pt="0 !important" px="0 !important" onClick={handleBackdropClick} $Close={close}>
      <StyledContainer
        onClick={(e) => e.stopPropagation()}
        style={close ? { maxHeight: '0%', overflow: 'hidden' } : {}}
      >
        <Page
          pb={[0, 0, '24px !important']}
          style={{ transition: '0.2s' }}
          zIndex={1000}
          width="100%"
          maxWidth={theme.siteWidth}
          maxHeight="100%"
          height={['100%', '', 'auto']}
          marginX="auto"
          overflowX={['unset', 'unset', 'unset', 'unset', 'unset', 'unset']}
          px={['0px !important', '0px', '16px !important', '', '64px !important']}
          py="0px !important"
        >
          <SearchLayout onClose={handleClose} />
        </Page>
        <Box
          ml={['12px', '', '24px']}
          position="absolute"
          right="12px"
          top="12px"
          zIndex={1000}
          display={['none', '', '', 'block']}
        >
          <IconButton id="close-desktop" onClick={handleBackdropClick}>
            <Icons.CloseIcon width="24px" />
          </IconButton>
        </Box>
      </StyledContainer>
    </StyledWrapper>
  )
}

const StyledWrapper = styled(Box)<{ $Close: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  display: flex;
  z-index: ${({ theme }) => theme.zIndices.modal};
  background: rgba(0, 0, 0, 0);
  backdrop-filter: unset;
  transition: 0.4s;
  ${({ theme }) => theme.mediaQueries.sm} {
    backdrop-filter: ${({ $Close }) => (!$Close ? 'blur(8px)' : 'blur(0px)')};
  }
`

const StyledContainer = styled(Box)`
  position: fixed;
  top: 0;
  left: 0;
  box-sizing: border-box;
  height: 100%;
  width: 100%;
  max-height: 100%;
  display: flex;
  z-index: ${({ theme }) => theme.zIndices.modal};
  animation-duration: 400ms;
  animation-name: ${hunnyKeyframes.fullSlideRightToLeft};

  background: ${({ theme }) => theme.colors.background};
  animation-name: none;
  transition: 0.4s;
  animation-duration: 0.4s;
  animation-name: open;

  ${({ theme }) => theme.mediaQueries.sm} {
    height: auto;
  }

  #searchLayout-topbar,
  #searchLayout-content {
    animation-duration: 0.6s;
    animation-name: searchInputOpen;
  }

  #searchLayout-content {
    animation-duration: 0.8s;
    animation-name: searchInputOpen;
  }

  @keyframes searchInputOpen {
    0% {
      opacity: 0;
      transform: translateX(-20px);
      pointer-events: none;
    }
    50% {
      opacity: 0;
      transform: translateX(-20px);
      pointer-events: none;
    }
    100% {
      opacity: 1;
      transform: translateX(0);
      pointer-events: all;
    }
  }

  @keyframes open {
    0% {
      transform: translateY(-100%);
    }
    100% {
      transform: translateY(0);
    }
  }
`

export default GameSearch
